import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FirewallBlockedError, UserCancelledError, formatBytes, type FileSizeCheck } from "./utils";
import { Square, AlertTriangle } from "lucide-react";
import { NoticeBox } from "@/components/NoticeBox";

export function VideoProcessingProgress({
  progress,
  statusMessage,
  onStop,
  heavyNote = false,
}: {
  progress: number;
  statusMessage: string;
  onStop?: () => void;
  /** Only the Compress tool has settings (Target Size, Strong) that make a run slower. */
  heavyNote?: boolean;
}) {
  const pct = Math.max(0, Math.min(100, Math.round(progress * 100)));
  return (
    <div className="space-y-2">
      <Progress value={pct} aria-label="Progress" />
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground" data-testid="video-progress">{statusMessage || "Processing..."}{pct > 0 && pct < 100 ? ` — ${pct}%` : ""}</p>
        {onStop && (
          <Button variant="outline" size="sm" onClick={onStop} className="gap-1.5 shrink-0 h-7">
            <Square className="h-3 w-3" /> Stop
          </Button>
        )}
      </div>
      {heavyNote && <p className="text-xs text-muted-foreground">
        Heavier settings (Target Size, Strong compression, longer clips) genuinely take longer to process — this can
        take a while and there's no automatic cutoff, so feel free to let it run. If it's taking longer than you'd
        like, use Stop above at any time.
      </p>}
    </div>
  );
}

/** Maps the shared error types to a clear, specific toast message rather
 * than a generic "something went wrong" for every video tool. */
export function showVideoError(err: unknown) {
  if (err instanceof FirewallBlockedError) {
    toast.error("Couldn't load the video engine — a firewall or restrictive network may be blocking it. Try a different network, or check with your IT team if you're on a work or school connection.");
  } else if (err instanceof UserCancelledError) {
    toast.info("Processing stopped.");
  } else if (err instanceof Error && /memory access out of bounds|RuntimeError/i.test(err.message)) {
    console.error("Video processing failed (WASM runtime error):", err);
    toast.error("The video engine ran into an internal error processing this file — this isn't a codec issue. Try again (the engine has been reset), or try a smaller file if it keeps happening.");
  } else {
    console.error("Video processing failed:", err);
    toast.error("Something went wrong processing this video. A common cause is a video encoded with a codec this tool doesn't support (some phone-recorded .mov files use one) — try re-exporting the video from your phone or camera app first (its own share/export option often re-encodes to a more universally compatible format), or check the browser console (F12) for the specific error.");
  }
}

/**
 * Shows the right message for a file's size relative to the person's
 * plan -- checked and shown AFTER a file is already selected, not as a
 * gate that blocks picking the file in the first place. Returns null for
 * a file that's fine, so callers can conditionally render this with
 * `{check && <FileSizeNotice check={check} isPro={isPro} />}`.
 */
export function FileSizeNotice({ check, isPro }: { check: FileSizeCheck; isPro: boolean }) {
  if (check.severity === "ok") return null;

  if (check.severity === "over-hard-limit") {
    return (
      <div className="flex gap-2 p-3 rounded-lg border border-destructive/30 bg-destructive/5">
        <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
        <p className="text-sm text-destructive">
          This file ({formatBytes(check.totalBytes)}) is too large to be processed here — the limit is{" "}
          {formatBytes(check.proMaxBytes)}, even on Pro. Try a smaller or more compressed file.
        </p>
      </div>
    );
  }

  // over-free-limit
  return (
    <NoticeBox className="flex gap-2">
      <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
      <p className="text-sm">
        You've selected a file over {formatBytes(check.freeMaxBytes)} ({formatBytes(check.totalBytes)}) — trial use
        supports up to {formatBytes(check.freeMaxBytes)}. Upgrade to Pro for up to {formatBytes(check.proMaxBytes)}.
      </p>
    </NoticeBox>
  );
}